import { useRef, useState } from "react";
import { api } from "../lib/api";
import { confirmDialog } from "../utils/dialogs";

/**
 * All the download / restore / upload handlers that power BackupRestoreModal.
 *
 * Everything the operator sees goes through here so the modal stays purely
 * presentational and the multi-step orchestration (safety-backup → preview
 * → restore, or full-PDF → walk-buckets → download-each-shard) is testable
 * in isolation.
 *
 * State surface:
 *   • busy, error, result           — shared spinner + banner state
 *   • pendingPreview, pendingFile   — set after Preview succeeds; the modal
 *                                     shows the diff card and unlocks Apply
 *   • setError, setResult           — exposed so consumers can clear/inject
 *
 * Returned ref goes straight on the hidden <input type="file"> node for
 * the restore flow. (The biometrics-only backup handlers were deleted on
 * 2026-02-24 — the full-DB backup is a strict superset.)
 */
export default function useBackupOperations() {
  const [busy, setBusy] = useState(false);
  // Narrow busy indicator: tracks WHICH flow is currently running so each
  // button can spin independently. "json" | "pdfs" | "restore" | null.
  // `busy` stays as the global disable gate; `busyFlow` drives the icon.
  const [busyFlow, setBusyFlow] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  // Restore preview state — populated by handlePreviewRestore, consumed
  // by handleApplyRestore and rendered by <RestorePreviewCard>.
  const [pendingPreview, setPendingPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const fullRestoreInputRef = useRef(null);

  // ---- Full DB JSON backup (any coach) --------------------------------
  //
  // Prefers the server-issued filename (Content-Disposition), so the
  // downloaded file always carries the exact UTC timestamp the server
  // stamped into the payload — clocks on operator laptops disagree just
  // often enough that client-generated timestamps produce confusing
  // collisions between preview and prod.
  const handleFullBackup = async ({ silent = false } = {}) => {
    if (!silent) setError("");
    setBusy(true);
    // When called silently (from the restore safety-backup step) we
    // don't want to steal the spinner from the Restore button — the
    // outer flow already set busyFlow="restore".
    if (!silent) setBusyFlow("json");
    try {
      const r = await api.get("/admin/full-backup", { responseType: "blob" });
      const filename = filenameFromDisposition(r.headers?.["content-disposition"])
        || `YCH-Health-Backup-${clientStamp()}.json`;
      triggerDownload(r.data, filename);
      if (!silent) setResult({ kind: "full-backup", filename, size: r.data.size });
      return { filename, size: r.data.size };
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Full backup failed";
      if (!silent) setError(msg);
      throw new Error(msg);
    } finally {
      setBusy(false);
      if (!silent) setBusyFlow(null);
    }
  };

  // ---- Full DB backup + PDFs (walks bucket shards) --------------------
  //
  // Kubernetes ingress caps single responses at ~60 s / ~150 MB, so we
  // fetch (a) the meta-only ZIP (JSON + manifest, ~30 MB) and (b) each
  // GridFS bucket's shards separately. The buckets endpoint tells us
  // how many pages each bucket needs. Every download uses the server
  // filename so the operator ends up with a set of timestamped files
  // they can restore in any order.
  const handleFullBackupWithPdfs = async () => {
    setError(""); setResult(null); setBusy(true); setBusyFlow("pdfs");
    const downloaded = [];
    try {
      // 1) Pull the bucket manifest so we know the shard count upfront.
      const bucketsResp = await api.get("/admin/full-backup-buckets");
      const buckets = bucketsResp.data?.buckets || [];

      // 2) Metadata ZIP (JSON + manifest, no PDFs).
      setResult({ kind: "full-backup-pdfs-progress", step: "meta", downloaded: [...downloaded] });
      const meta = await api.get("/admin/full-backup-zip?include_pdfs=false", { responseType: "blob" });
      const metaName = filenameFromDisposition(meta.headers?.["content-disposition"])
        || `YCH-Health-FullBackup-meta-${clientStamp()}.zip`;
      triggerDownload(meta.data, metaName);
      downloaded.push({ filename: metaName, size: meta.data.size });

      // 3) Every bucket, page by page.
      for (const b of buckets) {
        if (!b.files) continue;
        const pages = Math.max(1, b.recommended_pages || 1);
        const pageSize = b.page_size || 30;
        for (let p = 1; p <= pages; p++) {
          setResult({
            kind: "full-backup-pdfs-progress",
            step: `${b.bucket} p${p}/${pages}`,
            downloaded: [...downloaded],
          });
          const url = `/admin/full-backup-zip?bucket=${encodeURIComponent(b.bucket)}`
                    + `&page=${p}&page_size=${pageSize}`;
          const resp = await api.get(url, { responseType: "blob" });
          const name = filenameFromDisposition(resp.headers?.["content-disposition"])
            || `YCH-Health-FullBackup-${b.bucket}-p${p}of${pages}-${clientStamp()}.zip`;
          triggerDownload(resp.data, name);
          downloaded.push({ filename: name, size: resp.data.size });
        }
      }
      setResult({ kind: "full-backup-pdfs", downloaded });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Full+PDFs backup failed";
      setError(msg + (downloaded.length ? ` — ${downloaded.length} shard(s) downloaded before the error` : ""));
    } finally { setBusy(false); setBusyFlow(null); }
  };

  // ---- Restore preview (dry-run) --------------------------------------
  //
  // Two-step flow: (a) coach picks a file → we POST it to
  // /admin/full-restore-preview → the modal shows a per-collection
  // diff card. (b) coach clicks Apply → we run the safety backup and
  // POST /admin/full-restore with the same payload.
  //
  // File type dispatch:
  //   • `.json`  — parsed client-side and posted as JSON to the two
  //                existing endpoints (this is what "Restore from JSON"
  //                has always done).
  //   • `.zip`   — sent as `multipart/form-data` to the `-zip`
  //                variants. The server unpacks it, runs the JSON
  //                restore if a `YCH-Health-Backup-*.json` is inside,
  //                and pipes any `gridfs/*` entries back into GridFS
  //                preserving ObjectIds. Idempotent — safe to re-run.
  const handlePreviewRestore = async (e) => {
    setError(""); setResult(null); setPendingPreview(null); setPendingFile(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setBusyFlow("restore");
    try {
      const isZip = /\.zip$/i.test(file.name) || file.type === "application/zip";
      if (isZip) {
        const form = new FormData();
        form.append("zip_file", file);
        const r = await api.post("/admin/full-restore-preview-zip", form);
        const merged = {
          filename: file.name,
          _zip_source: file,
          pdfs: r.data?.pdfs || {},
          pdf_total: r.data?.pdf_total || 0,
          has_backup_json: !!r.data?.has_backup_json,
          // If the ZIP carried a JSON backup, splat the collection diff
          // in place so <RestorePreviewCard> renders the same table it
          // does for the JSON-only flow.
          ...(r.data?.collection_diff || {}),
        };
        setPendingPreview(merged);
        setPendingFile({ _zip: true, file });
      } else {
        const text = await file.text();
        const payload = JSON.parse(text);
        const r = await api.post("/admin/full-restore-preview", payload);
        setPendingPreview({ ...r.data, filename: file.name });
        setPendingFile(payload);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Preview failed");
    } finally {
      setBusy(false);
      setBusyFlow(null);
      if (fullRestoreInputRef.current) fullRestoreInputRef.current.value = "";
    }
  };

  const handleApplyRestore = async () => {
    if (!pendingFile || !pendingPreview) return;
    if (!(await confirmDialog(
      `Applying restore from ${pendingPreview.filename}\n\n` +
      "1. A safety backup of the CURRENT database will be downloaded first.\n" +
      "2. Then the selected backup will be merged into this environment.\n\n" +
      "Continue?",
      { confirmLabel: "Apply restore", danger: true },
    ))) return;
    setBusy(true); setBusyFlow("restore");
    let safetyName = null;
    try {
      setResult({ kind: "restore-progress", step: "safety-backup" });
      const safety = await handleFullBackup({ silent: true });
      safetyName = safety.filename;
      setResult({ kind: "restore-progress", step: "restoring", safetyBackupName: safetyName });
      if (pendingFile._zip) {
        const form = new FormData();
        form.append("zip_file", pendingFile.file);
        const r = await api.post("/admin/full-restore-zip?confirm_safety_backup=true", form);
        setResult({
          kind: "full-restore-zip",
          safetyBackupName: safetyName,
          ...r.data,
        });
      } else {
        const r = await api.post("/admin/full-restore?confirm_safety_backup=true", pendingFile);
        setResult({ kind: "full-restore", safetyBackupName: safetyName, ...r.data });
      }
      setPendingPreview(null); setPendingFile(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Full restore failed";
      setError(safetyName ? msg : `Safety backup failed — restore was NOT performed. ${msg}`);
      setResult(null);
    } finally { setBusy(false); setBusyFlow(null); }
  };

  const clearPendingRestore = () => {
    setPendingPreview(null); setPendingFile(null); setError("");
  };

  return {
    busy, busyFlow, error, result, pendingPreview,
    setError, setResult,
    fullRestoreInputRef,
    handleFullBackup, handleFullBackupWithPdfs,
    handlePreviewRestore, handleApplyRestore, clearPendingRestore,
  };
}

// ---- small helpers ---------------------------------------------------
function filenameFromDisposition(header) {
  if (!header) return null;
  const m = /filename="?([^";]+)"?/i.exec(header);
  return m ? m[1] : null;
}

function clientStamp() {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-") + "-UTC";
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a beat so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
